// 115 — UCI Pediátrica (Fase 4, Tanda 7a, ver PLAN.md "FASE 4 — spec
// completa" → "115 — Bitácora UCI Pediátrica"). Migrado del motor genérico
// compartido a un componente propio — mismo criterio que 113/114 (footer
// real confirmado "SNS-MSP/HCU-form.115/2021"). Alcance de esta tanda
// descrito en `bloques115Datos.ts`.
//
// FHIR: Encounter class=ACUTE + extensión de nivel de cuidado intensivo
// (mecanismo compartido), 1 Condition si el diagnóstico de ingreso trae
// texto (1 solo campo, igual que 114), y Talla/Peso real crean Observation
// reusando los LOINC de `vitalSigns.ts` (mismo criterio que la
// antropometría de 113).
import { useMedplum } from '@medplum/react-hooks';
import type { Observation } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloques115 } from './Bloques115';
import { diagramaGraficoConstantesUciDeEstado115, estadoInicialBitacora115, paresDeBitacora115, siluetaDeUlceras115 } from './bloques115Datos';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { EXT_NIVEL_CUIDADO_INTENSIVO } from './cuidadoIntensivo';
import { signoVitalPorClave, VITAL_SIGNS_CATEGORY } from './vitalSigns';

const CODIGO = '115';
const NIVEL_CUIDADO_INTENSIVO = 'UCI Pediátrica';

function numeroOIndefinido(texto: string): number | undefined {
  const valor = texto.trim() === '' ? undefined : Number(texto);
  return valor !== undefined && !Number.isNaN(valor) ? valor : undefined;
}

export function Formulario115({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.115');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { servicio: NIVEL_CUIDADO_INTENSIVO });

  const [bitacora, setBitacora] = useState(() => estadoInicialBitacora115());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const lote = new LoteTransaccional();
      const pares: Par[] = paresDeBitacora115(t, bitacora);
      const diagramasGraficoConstantesUci = [diagramaGraficoConstantesUciDeEstado115(bitacora.graficoConstantes)];
      const ahora = new Date().toISOString();

      if (encounter) {
        const actual = await medplum.readReference(encounter);
        const otrasExtensiones = (actual.extension ?? []).filter((e) => e.url !== EXT_NIVEL_CUIDADO_INTENSIVO);
        lote.actualizarEncounterAbierto({
          ...actual,
          class: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'ACUTE' },
          extension: [...otrasExtensiones, { url: EXT_NIVEL_CUIDADO_INTENSIVO, valueString: NIVEL_CUIDADO_INTENSIVO }],
        });
      }

      if (bitacora.diagnostico.texto.trim() !== '') {
        lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(bitacora.diagnostico), recordedDate: ahora });
      }

      const observaciones: Observation[] = [];
      const talla = numeroOIndefinido(bitacora.antropometria.talla);
      if (talla !== undefined) {
        const signo = signoVitalPorClave('talla');
        observaciones.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            valueQuantity: { value: talla, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
          }),
        );
      }
      const pesoReal = numeroOIndefinido(bitacora.antropometria.pesoReal);
      if (pesoReal !== undefined) {
        const signo = signoVitalPorClave('peso');
        observaciones.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            valueQuantity: { value: pesoReal, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
          }),
        );
      }

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp, undefined, undefined, undefined, undefined, undefined, diagramasGraficoConstantesUci, siluetaDeUlceras115(bitacora.siluetaUlceras));
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloques115
        antropometria={bitacora.antropometria}
        onChangeAntropometria={(v) => setBitacora({ ...bitacora, antropometria: v })}
        datosBitacora={bitacora.datosBitacora}
        onChangeDatosBitacora={(v) => setBitacora({ ...bitacora, datosBitacora: v })}
        diagnostico={bitacora.diagnostico}
        onChangeDiagnostico={(v) => setBitacora({ ...bitacora, diagnostico: v })}
        graficoConstantes={bitacora.graficoConstantes}
        onChangeGraficoConstantes={(v) => setBitacora({ ...bitacora, graficoConstantes: v })}
        hemodinamia={bitacora.hemodinamia}
        onChangeHemodinamia={(v) => setBitacora({ ...bitacora, hemodinamia: v })}
        grupos={bitacora.grupos}
        onChangeGrupos={(v) => setBitacora({ ...bitacora, grupos: v })}
        ingresos={bitacora.ingresos}
        onChangeIngresos={(v) => setBitacora({ ...bitacora, ingresos: v })}
        perdidas={bitacora.perdidas}
        onChangePerdidas={(v) => setBitacora({ ...bitacora, perdidas: v })}
        balanceUrinario={bitacora.balanceUrinario}
        onChangeBalanceUrinario={(v) => setBitacora({ ...bitacora, balanceUrinario: v })}
        totales={bitacora.totales}
        onChangeTotales={(v) => setBitacora({ ...bitacora, totales: v })}
        totalDia={bitacora.totalDia}
        onChangeTotalDia={(v) => setBitacora({ ...bitacora, totalDia: v })}
        ulceras={bitacora.ulceras}
        onChangeUlceras={(v) => setBitacora({ ...bitacora, ulceras: v })}
        siluetaUlceras={bitacora.siluetaUlceras}
        onChangeSiluetaUlceras={(v) => setBitacora({ ...bitacora, siluetaUlceras: v })}
        bradenBergstom={bitacora.bradenBergstom}
        onChangeBradenBergstom={(v) => setBitacora({ ...bitacora, bradenBergstom: v })}
        ramsay={bitacora.ramsay}
        onChangeRamsay={(v) => setBitacora({ ...bitacora, ramsay: v })}
        evans={bitacora.evans}
        onChangeEvans={(v) => setBitacora({ ...bitacora, evans: v })}
        planCuidadosGenerales={bitacora.planCuidadosGenerales}
        onChangePlanCuidadosGenerales={(v) => setBitacora({ ...bitacora, planCuidadosGenerales: v })}
        examenesEspeciales={bitacora.examenesEspeciales}
        onChangeExamenesEspeciales={(v) => setBitacora({ ...bitacora, examenesEspeciales: v })}
        interconsultas={bitacora.interconsultas}
        onChangeInterconsultas={(v) => setBitacora({ ...bitacora, interconsultas: v })}
        ventilacionAltaFrecuencia={bitacora.ventilacionAltaFrecuencia}
        onChangeVentilacionAltaFrecuencia={(v) => setBitacora({ ...bitacora, ventilacionAltaFrecuencia: v })}
        ventilacionConvencional={bitacora.ventilacionConvencional}
        onChangeVentilacionConvencional={(v) => setBitacora({ ...bitacora, ventilacionConvencional: v })}
        neurologico={bitacora.neurologico}
        onChangeNeurologico={(v) => setBitacora({ ...bitacora, neurologico: v })}
        pupilas={bitacora.pupilas}
        onChangePupilas={(v) => setBitacora({ ...bitacora, pupilas: v })}
        laboratorio={bitacora.laboratorio}
        onChangeLaboratorio={(v) => setBitacora({ ...bitacora, laboratorio: v })}
        gasometria={bitacora.gasometria}
        onChangeGasometria={(v) => setBitacora({ ...bitacora, gasometria: v })}
        tiss28={bitacora.tiss28}
        onChangeTiss28={(v) => setBitacora({ ...bitacora, tiss28: v })}
        prevencionInfecciones={bitacora.prevencionInfecciones}
        onChangePrevencionInfecciones={(v) => setBitacora({ ...bitacora, prevencionInfecciones: v })}
        procedimientos={bitacora.procedimientos}
        onChangeProcedimientos={(v) => setBitacora({ ...bitacora, procedimientos: v })}
        procedimientosOtros={bitacora.procedimientosOtros}
        onChangeProcedimientosOtros={(v) => setBitacora({ ...bitacora, procedimientosOtros: v })}
        dolorSedado={bitacora.dolorSedado}
        onChangeDolorSedado={(v) => setBitacora({ ...bitacora, dolorSedado: v })}
        responsableEnfermeria={bitacora.responsableEnfermeria}
        onChangeResponsableEnfermeria={(v) => setBitacora({ ...bitacora, responsableEnfermeria: v })}
      />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
